import React from 'react';
import styled from 'styled-components';
import OverlayDialog from '../../app/common/components/OverlayDialog';
import Spinner from '../../app/common/components/Spinner';
import { COLORS, SHADOWS } from '../../app/common/constants/styles';
import EditField from '../../app/common/fields/EditField';
import { PageId } from '../../config/constants/pageId.enum';
import { SupportedLanguage } from '../../config/constants/supportedLanguage.enum';
import { TranslationHelpers } from '../../i18n/translationHelpers';
import {
    destroyChallenge,
    startChallenge,
    updateChallengerLanguage,
} from '../../utils/challengerMain';
import OverlayHelper from '../../utils/overlayHelper';
import UpdateEmailFormContainer from './updateEmail/UpdateEmailFormContainer';
import UpdatePasswordFormContainer from './updatePassword/UpdatePasswordFormContainer';
import UpdateUsernameFormContainer from './updateUsername/UpdateUsernameFormContainer';
import ValueFormatters from '../../app/common/fields/valueFormatters';
import VerifyEmailForm from './verifyEmail/VerifyEmailForm';
import { RegionHelper } from '../../utils/RegionHelper';
import SecuritySection from './SecuritySection';
import { addRatEvent } from '../../utils/ratClickEvents';
import { oauthAPIService } from '../../api/oauth/oauthAPI';
import { history } from '../../utils/history';

export enum AccountSecurityFormType {
    EMAIL = 'EMAIL',
    PASSWORD = 'PASSWORD',
    RAKUTEN_ID = 'RAKUTEN_ID',
    VERIFY_EMAIL = 'VERIFY_EMAIL',
}

export type AccountSecurityProps = IAccountSecurityStateProps & IAccountSecurityDispatchProps;

export interface IAccountSecurityStateProps {
    member: any;
    isReady: boolean;
    isOverlayOpen: boolean;
    locale: SupportedLanguage;
    challengerPid: string;
    cancelMember: boolean;
    secretQuestionAnswerUrl: string;
    oamUrl: string;
    email2faFAQUrl: string;
    enableUserEmail2FAinOmni: string;
    showSecretQuestion?: boolean;
    overlayContent?: AccountSecurityFormType;
    sendOtpSuccess: boolean;
    isVerifyEmailFromUpdateEmail: boolean;
    tooManySendOtpRequests?: boolean;
    password: string;
    sessionRevocationEnabled: boolean;
    isSessionRevoked: boolean;
    signOutFromAllDevices?: boolean;
    errorRevokingSession: any;
    isEmail2FAEnabled: boolean;
    isTwIchiba: boolean;
}

export interface IAccountSecurityDispatchProps {
    closeAccountSecurityOverlay: () => any;
    openAccountSecurityOverlay: () => any;
    changeAccountSecurityOverlay: (content: AccountSecurityFormType | undefined) => any;
    handleSessionRevocation: () => any;
}

const Wrapper = styled.div`
    background-color: ${COLORS.white};
    border-radius: 4px;
    box-shadow: ${SHADOWS.level1};
    padding: 10px 40px;

    & > div {
        margin-top: 70px;
    }
`;

const SpinnerWrapper = styled.div`
    display: flex;
    min-height: 100%;
    justify-content: center;
    align-items: center;
`;

const Head = styled.h1`
    font-size: 1.75rem;
    font-weight: 400;
    margin: 18px auto 26px 0;
`;

export default class AccountSecurity extends React.Component<AccountSecurityProps> {
    constructor(props: AccountSecurityProps) {
        super(props);
        this.openOverlay = this.openOverlay.bind(this);
        this.getOverlayTitle = this.getOverlayTitle.bind(this);
        this.getOverlayContent = this.getOverlayContent.bind(this);
    }

    public componentDidMount() {
        window.scrollTo({ top: 0 });
        const { challengerPid } = this.props;
        startChallenge(challengerPid);

        // Check and handle any pending action from OMNI redirect
        this.checkAndHandlePendingAction();
    }

    public componentWillUnmount() {
        const { challengerPid } = this.props;
        destroyChallenge(challengerPid);
        OverlayHelper.closeHelper(PageId.profile);
    }

    public componentDidUpdate(prevProps: AccountSecurityProps): void {
        const { isOverlayOpen, locale, changeAccountSecurityOverlay, isReady } = this.props;

        if (!isOverlayOpen) {
            if (prevProps.isOverlayOpen !== isOverlayOpen) {
                changeAccountSecurityOverlay(undefined);
            }
            updateChallengerLanguage(locale);
        }

        // Handle pending action when component becomes ready
        if (!prevProps.isReady && isReady) {
            this.checkAndHandlePendingAction();
        }
    }

    public render() {
        const {
            closeAccountSecurityOverlay,
            isOverlayOpen,
            isReady,
            cancelMember,
            secretQuestionAnswerUrl,
            oamUrl,
            email2faFAQUrl,
            enableUserEmail2FAinOmni,
            showSecretQuestion,
            challengerPid,
            sessionRevocationEnabled,
            isSessionRevoked,
            errorRevokingSession,
            isTwIchiba,
        } = this.props;
        const { email, emailVerified, enabled2FA } = this.props.member;
        const displayUsername = this.getDisplayUsername();
        /* This is used to redirect the member to delete/unlink flow in MyR based on the region.
         * JP have cancel membership flow to delete the profile while EU have the similar flow via service & privacy page along with service unlink feature.
         */
        const cancelMembershipLink = RegionHelper.isEU()
            ? '/services-privacy'
            : '/account-security/delete';
        const cancelMembershipLinkText = RegionHelper.isEU()
            ? 'cancel.membership.link.eu'
            : 'cancel.membership.link';
        return (
            <Wrapper>
                {isOverlayOpen && (
                    <OverlayDialog
                        title={this.getOverlayTitle()}
                        isOpen={isOverlayOpen}
                        onClose={closeAccountSecurityOverlay}
                    >
                        {this.getOverlayContent()}
                    </OverlayDialog>
                )}
                {isReady ? (
                    <>
                        <Head>
                            {TranslationHelpers.getFormattedMessage(
                                'account.security.account_header',
                            )}
                        </Head>
                        <React.Fragment>
                            <p>
                                {TranslationHelpers.getFormattedMessage(
                                    'account.security.email2FA.info_text1',
                                )}
                            </p>
                            <EditField
                                id="editRakutenId"
                                label={TranslationHelpers.getMessageDescriptor('account.id')}
                                value={displayUsername}
                                onEdit={async () => {
                                    const sessionValid = await oauthAPIService.checkSessionValidity(
                                        'UPDATE_USERNAME',
                                    );
                                    if (sessionValid)
                                        this.openOverlay(AccountSecurityFormType.RAKUTEN_ID);
                                }}
                                qaId="rakuten-id"
                            />
                            <EditField
                                id="editEmail"
                                label={TranslationHelpers.getMessageDescriptor('label.email')}
                                value={ValueFormatters.toLowercase(email)}
                                onEdit={async () => {
                                    const sessionValid = await oauthAPIService.checkSessionValidity(
                                        'UPDATE_EMAIL',
                                    );
                                    if (sessionValid) {
                                        this.openOverlay(AccountSecurityFormType.EMAIL);
                                    }
                                }}
                                qaId="email"
                                emailVerified={emailVerified}
                                onVerify={() => {
                                    addRatEvent('email_verification', 'verify_email');
                                    this.openOverlay(AccountSecurityFormType.VERIFY_EMAIL);
                                    destroyChallenge(challengerPid);
                                }}
                            />
                            <EditField
                                id="editPassword"
                                label={TranslationHelpers.getMessageDescriptor('label.password')}
                                value="******"
                                onEdit={async () => {
                                    addRatEvent('password_update', 'password_edit');
                                    const sessionValid = await oauthAPIService.checkSessionValidity(
                                        'UPDATE_PASSWORD',
                                    );
                                    if (sessionValid)
                                        this.openOverlay(AccountSecurityFormType.PASSWORD);
                                }}
                                qaId="password"
                            />
                        </React.Fragment>
                        <SecuritySection
                            cancelMember={cancelMember}
                            cancelMembershipLink={cancelMembershipLink}
                            cancelMembershipLinkText={cancelMembershipLinkText}
                            sessionRevocationEnabled={sessionRevocationEnabled}
                            isSessionRevoked={isSessionRevoked}
                            enabled2FA={enabled2FA}
                            errorRevokingSession={errorRevokingSession}
                            showSecretQuestion={showSecretQuestion}
                            secretQuestionAnswerUrl={secretQuestionAnswerUrl}
                            oamUrl={oamUrl}
                            email2faFAQUrl={email2faFAQUrl}
                            enableUserEmail2FAinOmni={enableUserEmail2FAinOmni}
                            isTwIchiba={isTwIchiba}
                            handleSessionRevocation={this.props.handleSessionRevocation}
                        ></SecuritySection>
                    </>
                ) : (
                    <SpinnerWrapper>
                        <Spinner size={50} />
                    </SpinnerWrapper>
                )}
            </Wrapper>
        );
    }

    private getDisplayUsername() {
        const { username, email } = this.props.member;
        if (!username) {
            return '';
        }

        if (username.toLowerCase() === email.toLowerCase()) {
            return TranslationHelpers.getFormattedMessage('account.username.same.email.text');
        }

        return username.toLowerCase();
    }

    private getOverlayTitle() {
        const {
            member,
            overlayContent,
            sendOtpSuccess,
            isVerifyEmailFromUpdateEmail,
            tooManySendOtpRequests,
        } = this.props;

        switch (overlayContent) {
            case AccountSecurityFormType.RAKUTEN_ID:
                return member.username
                    ? TranslationHelpers.getMessageDescriptor('app.update_rak_id')
                    : TranslationHelpers.getMessageDescriptor('app.choose_rak_id');
            case AccountSecurityFormType.EMAIL:
                return TranslationHelpers.getMessageDescriptor('app.update_email');
            case AccountSecurityFormType.PASSWORD:
                return TranslationHelpers.getMessageDescriptor('app.update_pass');
            case AccountSecurityFormType.VERIFY_EMAIL:
                return !sendOtpSuccess
                    ? TranslationHelpers.getMessageDescriptor('email.verify.sendOtpFailedTitle')
                    : tooManySendOtpRequests
                    ? TranslationHelpers.getMessageDescriptor('email.verify.limitTitle')
                    : isVerifyEmailFromUpdateEmail
                    ? TranslationHelpers.getMessageDescriptor('email.verify.newEmailTitle')
                    : TranslationHelpers.getMessageDescriptor('email.verify.title');
            default:
                return undefined;
        }
    }

    private getOverlayContent() {
        const { overlayContent } = this.props;

        switch (overlayContent) {
            case AccountSecurityFormType.RAKUTEN_ID:
                return <UpdateUsernameFormContainer />;
            case AccountSecurityFormType.EMAIL:
                return <UpdateEmailFormContainer />;
            case AccountSecurityFormType.PASSWORD:
                return <UpdatePasswordFormContainer />;
            case AccountSecurityFormType.VERIFY_EMAIL:
                return <VerifyEmailForm />;
            default:
                return undefined;
        }
    }

    private openOverlay(content: AccountSecurityFormType) {
        const { changeAccountSecurityOverlay, openAccountSecurityOverlay } = this.props;
        changeAccountSecurityOverlay(content);
        openAccountSecurityOverlay();
    }

    private mapOperationToFormType(operation: string): AccountSecurityFormType | null {
        // Convert to uppercase to match operation constants
        switch (operation.toUpperCase()) {
            case 'UPDATE_USERNAME':
                return AccountSecurityFormType.RAKUTEN_ID;
            case 'UPDATE_EMAIL':
                return AccountSecurityFormType.EMAIL;
            case 'UPDATE_PASSWORD':
                return AccountSecurityFormType.PASSWORD;
            default:
                return null;
        }
    }

    private checkAndHandlePendingAction() {
        const { isReady } = this.props;

        if (!isReady) return;

        // Restore user's action from query parameter (preserved through OMNI redirect)
        const urlParams = new URLSearchParams(window.location.search);
        const pendingAction = urlParams.get('pending_action');

        if (pendingAction) {
            const formType = this.mapOperationToFormType(pendingAction);
            if (formType) {
                this.openOverlay(formType);

                // Clean up URL by removing pending_action parameter
                urlParams.delete('pending_action');
                const newSearch = urlParams.toString();
                const pathname = window.location.pathname.substring(process.env.PUBLIC_URL.length);
                const newPath = newSearch ? `${pathname}?${newSearch}` : pathname;
                history.replace(newPath);
            }
        }
    }
}
